import { useState } from "react";
import { 
  HelpCircle, 
  ChevronDown, 
  ChevronRight,
  LayoutDashboard,
  Wallet,
  PieChart,
  Calendar,
  CalendarRange,
  Target,
  Layers,
  Settings,
  BookOpen,
  Plane
} from "lucide-react";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";

const GUIDES = [
  {
    id: "getting-started",
    icon: BookOpen,
    title: "Getting Started",
    content: `
**Welcome to LifeLedger!** Here's how to get started:

1. **Set up your Accounts** - Add all your savings and investment accounts with their current balances
2. **Define Monthly Budget** - Enter your typical monthly income, expenses, and savings
3. **Add Annual Items** - Include annual income (bonus) and expenses (insurance, fees)
4. **Create Future Plans** - Use the data to forecast your wealth growth
5. **Plan Holidays** - Budget and track your vacation expenses

The app will automatically calculate your savings and help you visualize your financial future.
    `
  },
  {
    id: "accounts",
    icon: Wallet,
    title: "Accounts Page",
    content: `
**Purpose:** Track all your savings and investment accounts.

**How to use:**
1. Click "Add Account" to create a new account
2. Enter the account name (e.g., "Emergency Fund", "Retirement Account")
3. Select the account type: Savings or Investment
4. Enter the current balance and currency
5. Set the category (Personal, Child, Joint) to organize accounts
6. **Important:** Set the "Annual Return %" for investment accounts - this is used in Future Planning

**Tips:**
- Use different categories for family members (e.g., "Child A", "Child B")
- The total balance is automatically converted to your default currency
- Update balances regularly to keep forecasts accurate
    `
  },
  {
    id: "investments",
    icon: PieChart,
    title: "Investments Page",
    content: `
**Purpose:** Define the asset allocation for each investment account.

**How to use:**
1. Select an investment account from the dropdown
2. Set the percentage allocation for each asset class:
   - **Equity:** Stocks, equity mutual funds
   - **Debt:** Bonds, fixed deposits
   - **Commodities:** Gold, silver, oil
   - **Cash:** Money market, liquid funds
3. The percentages must add up to 100%
4. Click "Save Allocation" to store

**Tips:**
- Review and rebalance allocations periodically
- The portfolio overview shows your overall asset distribution
    `
  },
  {
    id: "monthly",
    icon: Calendar,
    title: "Monthly Budget Page",
    content: `
**Purpose:** Define your typical monthly income, expenses, and savings.

**How to use:**
1. **Add Income items:** Salary, side income, freelance work
2. **Add Expense items:** Rent, utilities, groceries, entertainment
3. **Add Savings/Investment items:** SIP contributions, recurring investments

**Understanding the numbers:**
- **Monthly Income:** Total of all income items
- **Monthly Expenses:** Total of all expense items
- **Monthly Savings:** Total of savings/investment items (→ goes to Future Planning)
- **Net Balance:** Income - Expenses - Savings (leftover cash)

**Important:** Monthly Savings is what you explicitly put aside for investments/savings. The Net Balance × 12 flows to Annual Budget.
    `
  },
  {
    id: "annual",
    icon: CalendarRange,
    title: "Annual Budget Page",
    content: `
**Purpose:** Track annual income and expenses that don't occur monthly.

**How to use:**
1. Review the auto-calculated "Net Balance × 12" from Monthly Budget
2. Add annual income: Bonus, 13th month salary, dividends, tax refunds
3. Add annual expenses: Insurance premiums, school fees, vacation, car maintenance

**Understanding the calculation:**
\`\`\`
Annual Savings = (Net Balance × 12) + Annual Income - Annual Expenses
\`\`\`

This Annual Savings value is used in Future Planning as the yearly contribution.

**Tips:**
- Include all predictable annual income and expenses
- Don't forget insurance renewals and subscription fees
    `
  },
  {
    id: "planning",
    icon: Target,
    title: "Future Planning Page",
    content: `
**Purpose:** Create financial forecasts to visualize wealth growth.

**How to use:**
1. Click "Create Plan" to start a new forecast
2. **Select Accounts:** Choose which accounts to include (e.g., only personal accounts for retirement)
3. Click "Calculate from Selected" to auto-fill values:
   - Starting Balance: Sum of selected accounts
   - Monthly Savings: From Monthly Budget savings items
   - Annual Savings: From Annual Budget calculation
   - Weighted Return: Average return from selected accounts
4. Adjust values if needed
5. Set the forecast period (e.g., 10, 20, 30 years)
6. Add one-time events (future expenses like education, or income like inheritance)

**Understanding the forecast:**
- Returns are compounded annually
- Monthly savings earn ~half year of returns (since they come throughout the year)
- The chart shows your projected wealth trajectory
    `
  },
  {
    id: "holiday-planning",
    icon: Plane,
    title: "Holiday Planning Page",
    content: `
**Purpose:** Plan and track your vacation budgets throughout the year.

**How to use:**
1. Click "Add Holiday Plan" to create a new vacation budget
2. Enter the holiday name (e.g., "Summer Vacation 2025", "Christmas Trip")
3. Select the year for the holiday
4. Set the duration in days
5. **Budget Categories:** Enter estimated costs for:
   - **Travel:** Flights, trains, car rentals
   - **Hotel:** Accommodation costs
   - **Food:** Restaurants, groceries while traveling
   - **Activities:** Tours, attractions, entertainment
   - **Miscellaneous:** Shopping, tips, unexpected expenses
6. After the trip, update the **Actual Costs** to track real spending

**Understanding the summary:**
- **Total Budget:** Sum of all estimated category costs
- **Total Actual:** Sum of actual expenses (after the trip)
- **Variance:** Difference between budget and actual (positive = under budget)

**Tips:**
- Create holiday plans at the start of the year
- Update actual costs as you spend during the trip
- Use the variance to improve future budgeting
- Consider adding a buffer in the miscellaneous category
    `
  },
  {
    id: "allocations",
    icon: Layers,
    title: "Allocations Page",
    content: `
**Purpose:** Track monthly investment allocations by instrument.

**How to use:**
1. Select a year and month
2. Add allocation items with:
   - Instrument type (Stocks, ETFs, Bonds, etc.)
   - Description (specific fund or stock name)
   - Amount invested
3. View the pie chart breakdown

**Tips:**
- Use this to track your actual monthly investments
- Compare with your target allocations in the Investments page
    `
  },
  {
    id: "settings",
    icon: Settings,
    title: "Settings Page",
    content: `
**Purpose:** Configure app preferences.

**Available settings:**
- **Default Currency:** Set your primary currency (EUR, USD, INR, etc.)
  - All balances will be converted to this currency for display
  - Exchange rates are fetched in real-time

**Tips:**
- Choose the currency you use most for daily expenses
- Multi-currency accounts are fully supported
    `
  }
];

const FAQS = [
  {
    question: "How do I install LifeLedger on my phone?",
    answer: "LifeLedger is a Progressive Web App (PWA). On iPhone: Open in Safari, tap the Share button, then 'Add to Home Screen'. On Android: Open in Chrome, tap the 3-dot menu, then 'Add to Home Screen' or 'Install App'. The app will work offline and feel like a native app!"
  },
  {
    question: "How do I track accounts in different currencies?",
    answer: "Simply set the currency when creating each account. The app automatically converts all balances to your default currency using real-time exchange rates."
  },
  {
    question: "What's the difference between Monthly Savings and Net Balance?",
    answer: "Monthly Savings = money you explicitly set aside for investments/savings (SIPs, etc.). Net Balance = what's left after all income, expenses, and savings. Net Balance × 12 flows to Annual Budget."
  },
  {
    question: "How are returns calculated in Future Planning?",
    answer: "Starting balance earns the full year's return. Monthly and annual contributions earn approximately half a year's return (since they're added throughout the year). Returns compound annually."
  },
  {
    question: "Can I create separate plans for different goals?",
    answer: "Yes! Create multiple plans with different account selections. For example: select only personal accounts for retirement planning, only child accounts for education planning."
  },
  {
    question: "How do I account for one-time future expenses?",
    answer: "In Future Planning, you can add 'One-Time Events' for any year. These can be expenses (house purchase, education) or income (inheritance, property sale)."
  },
  {
    question: "What is the Annual Return % in Accounts?",
    answer: "This is the expected annual return for that account. For savings accounts, it might be 3-4%. For investment accounts, it could be 8-12% depending on your asset allocation. This is used to calculate weighted returns in Future Planning."
  },
  {
    question: "How often should I update my data?",
    answer: "Update account balances monthly or quarterly. Review and adjust Monthly/Annual Budget items when your financial situation changes (new job, new expenses, etc.)."
  },
  {
    question: "Can multiple family members use this app?",
    answer: "Yes! Use the 'Category' field in Accounts to organize by family member (Personal, Child A, Child B, Joint). You can then select specific categories when creating future plans."
  },
  {
    question: "How do I plan a holiday budget?",
    answer: "Go to the Holiday Planning page and create a new plan. Enter your estimated costs for travel, hotel, food, activities, and miscellaneous expenses. After your trip, update the actual costs to see how well you budgeted."
  },
  {
    question: "Can I track multiple holidays in a year?",
    answer: "Yes! You can create as many holiday plans as you need. Each plan is independent and can be for different destinations or purposes (summer vacation, weekend getaway, Christmas trip, etc.)."
  },
  {
    question: "What's the best way to budget for holidays?",
    answer: "Research typical costs before your trip, add 10-20% buffer in the miscellaneous category for unexpected expenses, and update actual costs during/after the trip. Compare variances to improve future budgets."
  }
];

export default function Help({ user }) {
  const [expandedGuide, setExpandedGuide] = useState("getting-started");
  const [expandedFaq, setExpandedFaq] = useState(null);

  return (
    <Layout user={user} pageTitle="Help & Guide">
      <div className="space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-white mb-1">Help & Guide</h1>
          <p className="text-gray-400">
            Learn how to use LifeLedger effectively with step-by-step guides and FAQs.
          </p>
        </div>

        {/* Quick Overview */}
        <Card className="bg-gradient-to-br from-green-500/10 to-blue-500/10 border-green-500/20">
          <CardContent className="p-6">
            <h2 className="text-lg font-bold text-white mb-3">Quick Overview</h2>
            <p className="text-gray-300 mb-4">
              LifeLedger helps you track your finances and plan for the future. The flow is simple:
            </p>
            <div className="flex flex-wrap gap-2 text-sm">
              <span className="px-3 py-1 bg-green-500/20 text-green-400 rounded-full">1. Add Accounts</span>
              <span className="text-gray-500">→</span>
              <span className="px-3 py-1 bg-blue-500/20 text-blue-400 rounded-full">2. Set Monthly Budget</span>
              <span className="text-gray-500">→</span>
              <span className="px-3 py-1 bg-purple-500/20 text-purple-400 rounded-full">3. Add Annual Items</span>
              <span className="text-gray-500">→</span>
              <span className="px-3 py-1 bg-amber-500/20 text-amber-400 rounded-full">4. Create Future Plans</span>
            </div>
          </CardContent>
        </Card>

        {/* Page Guides */}
        <div>
          <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-green-400" />
            Page-by-Page Guide
          </h2>
          <div className="space-y-3">
            {GUIDES.map((guide) => {
              const Icon = guide.icon;
              const isExpanded = expandedGuide === guide.id;
              return (
                <Card key={guide.id} className="bg-card border-border">
                  <div
                    className="p-4 flex items-center justify-between cursor-pointer hover:bg-secondary/50 transition-colors"
                    onClick={() => setExpandedGuide(isExpanded ? null : guide.id)}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-green-500/10 flex items-center justify-center">
                        <Icon className="w-5 h-5 text-green-400" />
                      </div>
                      <span className="font-medium text-white">{guide.title}</span>
                    </div>
                    {isExpanded ? (
                      <ChevronDown className="w-5 h-5 text-gray-500" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-gray-500" />
                    )}
                  </div>
                  {isExpanded && (
                    <CardContent className="pt-0 pb-4 px-4">
                      <div className="pl-13 border-l-2 border-green-500/20 ml-5 pl-8">
                        <div className="prose prose-invert prose-sm max-w-none">
                          {guide.content.split('\n').map((line, i) => {
                            if (line.startsWith('**') && line.endsWith('**')) {
                              return <h4 key={i} className="text-green-400 font-semibold mt-3 mb-1">{line.replace(/\*\*/g, '')}</h4>;
                            }
                            if (line.startsWith('- ')) {
                              return <p key={i} className="text-gray-300 ml-4 my-1">• {line.substring(2)}</p>;
                            }
                            if (line.match(/^\d+\./)) {
                              return <p key={i} className="text-gray-300 my-1">{line}</p>;
                            }
                            if (line.trim() === '') return null;
                            return <p key={i} className="text-gray-400 my-2">{line}</p>;
                          })}
                        </div>
                      </div>
                    </CardContent>
                  )}
                </Card>
              );
            })}
          </div>
        </div>

        {/* FAQ Section */}
        <div>
          <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <HelpCircle className="w-5 h-5 text-blue-400" />
            Frequently Asked Questions
          </h2>
          <div className="space-y-3">
            {FAQS.map((faq, index) => {
              const isExpanded = expandedFaq === index;
              return (
                <Card key={index} className="bg-card border-border">
                  <div
                    className="p-4 flex items-center justify-between cursor-pointer hover:bg-secondary/50 transition-colors"
                    onClick={() => setExpandedFaq(isExpanded ? null : index)}
                  >
                    <span className="font-medium text-white pr-4">{faq.question}</span>
                    {isExpanded ? (
                      <ChevronDown className="w-5 h-5 text-gray-500 flex-shrink-0" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-gray-500 flex-shrink-0" />
                    )}
                  </div>
                  {isExpanded && (
                    <CardContent className="pt-0 pb-4 px-4">
                      <p className="text-gray-400 border-l-2 border-blue-500/20 pl-4">
                        {faq.answer}
                      </p>
                    </CardContent>
                  )}
                </Card>
              );
            })}
          </div>
        </div>
      </div>
    </Layout>
  );
}
